/**
 * Human-readable formatting for AI rule conditions and actions.
 */
import { defaultEnglishTranslations } from './rule-types';
import { extractKeywordFromRegex } from './rule-keyword-utils';
import { DATE_BEFORE_OPERATORS, DATE_AFTER_OPERATORS, AMOUNT_LESS_OPERATORS, AMOUNT_LESS_EQUAL_OPERATORS, AMOUNT_GREATER_OPERATORS, AMOUNT_GREATER_EQUAL_OPERATORS, AMOUNT_LOWER_BOUND_OPERATORS, AMOUNT_UPPER_BOUND_OPERATORS, } from './rule-operator-aliases';
// ─── Helpers ─────────────────────────────────────────────────────────────────
/**
 * Normalizes a field name by removing JSONPath prefix
 */
export function normalizeFieldName(field) {
    if (field.startsWith('$.')) {
        return field.slice(2);
    }
    return field;
}
/**
 * Formats an ISO date string for human-readable display.
 */
function formatDateValue(isoDate) {
    try {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(new Date(isoDate));
    }
    catch {
        return isoDate;
    }
}
/**
 * Formats a numeric value for display, falling back to the raw string.
 */
function formatAmountValue(value) {
    const num = typeof value === 'number' ? value : parseFloat(value.toString());
    return isNaN(num) ? value.toString() : num.toString();
}
// ─── Description operator alias sets (local, not worth sharing) ──────────────
const DESC_NOT_CONTAINS_OPERATORS = new Set([
    'not_contains',
    'not-contains',
    'notContains',
    'not-matches',
    'not_matches',
]);
const DESC_STARTS_OPERATORS = new Set([
    'starts',
    'starts_with',
    'starts-with',
    'startsWith',
]);
const DESC_ENDS_OPERATORS = new Set([
    'ends',
    'ends_with',
    'ends-with',
    'endsWith',
]);
// ─── Condition text builders ─────────────────────────────────────────────────
/**
 * Builds a human-readable condition text for a single condition.
 * Operates on raw (un-normalized) operators throughout.
 */
export function buildConditionText(condition, patternType, translations) {
    const field = normalizeFieldName(condition.field);
    const operator = condition.operator;
    const value = condition.value.toString();
    if (field === 'description') {
        let keyword = value;
        if (operator === 'matches' || operator === 'contains') {
            if (operator === 'matches') {
                keyword = extractKeywordFromRegex(value);
            }
            switch (patternType) {
                case 'exact':
                    return `${translations.conditions.descriptionContainsExactly} "${keyword}"`;
                case 'smart':
                    return `${translations.conditions.descriptionContainsInOrder} "${keyword}"`;
                case 'any':
                    return `${translations.conditions.descriptionContainsAny} "${keyword}"`;
                default:
                    return `${translations.conditions.descriptionContains} "${keyword}"`;
            }
        }
        if (DESC_NOT_CONTAINS_OPERATORS.has(operator)) {
            keyword = extractKeywordFromRegex(keyword);
            return `${translations.conditions.descriptionDoesNotContain} "${keyword}"`;
        }
        if (DESC_STARTS_OPERATORS.has(operator)) {
            return `${translations.conditions.descriptionStartsWith} "${keyword}"`;
        }
        if (DESC_ENDS_OPERATORS.has(operator)) {
            return `${translations.conditions.descriptionEndsWith} "${keyword}"`;
        }
        return `${translations.conditions.descriptionContains} "${keyword}"`;
    }
    if (field === 'amount' || field === 'absoluteAmount') {
        const formatted = formatAmountValue(condition.value);
        if (AMOUNT_GREATER_OPERATORS.has(operator)) {
            return `${translations.conditions.amountGreaterThan} ${formatted}`;
        }
        if (AMOUNT_GREATER_EQUAL_OPERATORS.has(operator)) {
            return `${translations.conditions.amountGreaterOrEqual} ${formatted}`;
        }
        if (AMOUNT_LESS_OPERATORS.has(operator)) {
            return `${translations.conditions.amountLessThan} ${formatted}`;
        }
        if (AMOUNT_LESS_EQUAL_OPERATORS.has(operator)) {
            return `${translations.conditions.amountLessOrEqual} ${formatted}`;
        }
        return `${translations.conditions.amountEquals} ${formatted}`;
    }
    if (field === 'transaction_date') {
        if (DATE_AFTER_OPERATORS.has(operator)) {
            return `${translations.conditions.dateStartingFrom} ${formatDateValue(value)}`;
        }
        if (DATE_BEFORE_OPERATORS.has(operator)) {
            return `${translations.conditions.dateUntil} ${formatDateValue(value)}`;
        }
        return `${translations.conditions.dateStartingFrom} ${formatDateValue(value)}`;
    }
    if (field === 'currency') {
        return `${translations.conditions.currencyIs} ${value}`;
    }
    if (field === 'account') {
        return `${translations.conditions.accountIs} ${value}`;
    }
    return `${field} ${operator} "${value}"`;
}
/**
 * Builds a human-readable action text
 */
export function buildActionText(action, translations, categoryNameResolver, accountNameResolver) {
    const actionValue = action.actionValue.toString();
    switch (action.actionType) {
        case 'set_category': {
            const categoryName = categoryNameResolver?.(actionValue) || actionValue;
            return `${translations.actions.setCategory} "${categoryName}"`;
        }
        case 'add_tag':
            return `${translations.actions.addTag} "${actionValue}"`;
        case 'set_note':
            return `${translations.actions.addNote} "${actionValue}"`;
        case 'set_account': {
            const accountName = accountNameResolver?.(actionValue) || actionValue;
            return `${translations.actions.setAccount} "${accountName}"`;
        }
        case 'set_merchant':
            return `${translations.actions.setMerchant} "${actionValue}"`;
        default:
            return `${action.actionType} "${actionValue}"`;
    }
}
// ─── Pair merging ────────────────────────────────────────────────────────────
/**
 * Finds the index of a complementary condition (for "between" merging).
 * Returns -1 if no complement is found.
 */
function findComplementIndex(items, afterIndex, used, fieldMatch, complementSet) {
    return items.findIndex((b, j) => j > afterIndex &&
        !used.has(j) &&
        fieldMatch(normalizeFieldName(b.field)) &&
        complementSet.has(b.operator));
}
/**
 * Merges paired date or amount conditions into a single "between" text.
 * Unpaired conditions are rendered individually.
 */
function buildMergedConditionTexts(items, patternType, translations) {
    const used = new Set();
    const results = [];
    for (let i = 0; i < items.length; i++) {
        if (used.has(i))
            continue;
        const a = items[i];
        const aField = normalizeFieldName(a.field);
        const aOp = a.operator;
        // Date between: pair an after-op with a before-op (or vice versa)
        if (aField === 'transaction_date') {
            const isAfter = DATE_AFTER_OPERATORS.has(aOp);
            const isBefore = DATE_BEFORE_OPERATORS.has(aOp);
            if (isAfter || isBefore) {
                const complementSet = isAfter
                    ? DATE_BEFORE_OPERATORS
                    : DATE_AFTER_OPERATORS;
                const pairIdx = findComplementIndex(items, i, used, (f) => f === 'transaction_date', complementSet);
                if (pairIdx !== -1) {
                    used.add(i);
                    used.add(pairIdx);
                    const fromVal = isAfter
                        ? a.value.toString()
                        : items[pairIdx].value.toString();
                    const toVal = isAfter
                        ? items[pairIdx].value.toString()
                        : a.value.toString();
                    if (formatDateValue(fromVal) === formatDateValue(toVal)) {
                        results.push(`**${translations.conditions.dateOn} ${formatDateValue(fromVal)}**`);
                    }
                    else {
                        results.push(`**${translations.conditions.dateBetween} ${formatDateValue(fromVal)} ${translations.and} ${formatDateValue(toVal)}**`);
                    }
                    continue;
                }
            }
        }
        // Amount between: pair a lower-bound with an upper-bound (or vice versa)
        if (aField === 'amount' || aField === 'absoluteAmount') {
            const isLower = AMOUNT_LOWER_BOUND_OPERATORS.has(aOp);
            const isUpper = AMOUNT_UPPER_BOUND_OPERATORS.has(aOp);
            if (isLower || isUpper) {
                const complementSet = isLower
                    ? AMOUNT_UPPER_BOUND_OPERATORS
                    : AMOUNT_LOWER_BOUND_OPERATORS;
                const isAmountField = (f) => f === 'amount' || f === 'absoluteAmount';
                const pairIdx = findComplementIndex(items, i, used, isAmountField, complementSet);
                if (pairIdx !== -1) {
                    used.add(i);
                    used.add(pairIdx);
                    const lowVal = isLower ? a.value : items[pairIdx].value;
                    const highVal = isLower ? items[pairIdx].value : a.value;
                    if (formatAmountValue(lowVal) === formatAmountValue(highVal)) {
                        results.push(`**${translations.conditions.amountEquals} ${formatAmountValue(lowVal)}**`);
                    }
                    else {
                        results.push(`**${translations.conditions.amountBetween} ${formatAmountValue(lowVal)} ${translations.and} ${formatAmountValue(highVal)}**`);
                    }
                    continue;
                }
            }
        }
        // No pair found — render individually
        used.add(i);
        const text = buildConditionText(a, patternType, translations);
        results.push(`**${text}**`);
    }
    return results;
}
// ─── Main entry point ────────────────────────────────────────────────────────
/**
 * Converts an AICondition schema to a human-readable string with formatting markers.
 */
export function aiConditionToHumanReadable(condition, options) {
    const translations = options?.translations || defaultEnglishTranslations;
    const categoryNameResolver = options?.categoryNameResolver;
    const accountNameResolver = options?.accountNameResolver;
    const isAndCondition = 'and' in condition;
    const conditionItems = isAndCondition
        ? condition.and
        : condition.or;
    const conjunction = isAndCondition ? translations.and : translations.or;
    const actions = condition.result.value;
    let patternType = 'exact';
    for (const action of actions) {
        if (action.patternType) {
            patternType = action.patternType;
            break;
        }
    }
    const conditionTexts = buildMergedConditionTexts(conditionItems, patternType, translations);
    const actionTexts = actions.map((action) => {
        const text = buildActionText(action, translations, categoryNameResolver, accountNameResolver);
        return `##${text}##`;
    });
    const conditionsStr = conditionTexts.join(` ${conjunction} `);
    const actionsStr = actionTexts.join(` ${translations.and} `);
    return `${translations.everyTime} ${conditionsStr} ${translations.then} ${actionsStr}`;
}
